import { SmgElement } from '../core/smg-element';
import { each, html, when, type SafeHtml } from '../core/safe-html';
import { nfmt } from '../core/format';
import type { BrandProfile, BrandProfileInput } from '../models/brand';
import { brands as brandService } from '../services/brand-service';
import { session } from '../services/auth-service';
import { showToast } from '../services/toast-service';
import { confirmDialog } from '../services/confirm-service';

const NEW_SCRIPT_TEMPLATE = `#!/bin/sh
set -e
curl -sfL https://cdn.smg.com.tr/player/install.sh | sh
systemctl enable --now smg-player
smg-player --brand "$BRAND" --serial "$SERIAL"`;

interface BrandForm extends BrandProfileInput {
  /** 'new' = henüz kaydedilmemiş; değilse düzenlenen profilin orijinal adı */
  original: string | 'new';
}

/** Marka & imaj yönetimi: profil listesi + düzenleme formu + script alanı. */
export class BrandsView extends SmgElement {
  private list: BrandProfile[] = [];
  private loading = true;
  private error = '';
  private form: BrandForm | null = null;

  protected override onConnected(): void {
    this.wireActs();
    void this.load();
  }

  private async load(selectName?: string): Promise<void> {
    this.loading = true;
    this.error = '';
    this.render();
    try {
      this.list = await brandService.list();
      const target =
        (selectName && this.list.find((b) => b.name === selectName)) || this.list[0] || null;
      this.form = target ? this.formFrom(target) : null;
    } catch {
      this.error = 'Marka listesi alınamadı.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private formFrom(b: BrandProfile): BrandForm {
    return {
      original: b.name,
      name: b.name,
      imageFile: b.imageFile,
      version: b.version,
      script: b.script,
    };
  }

  private canEdit(): boolean {
    return session.get().claims?.brands ?? false;
  }

  private wireActs(): void {
    this.onAct('click', {
      pick: (el) => {
        const b = this.list.find((x) => x.name === el.dataset['brand']);
        if (b) {
          this.form = this.formFrom(b);
          this.render();
        }
      },
      newBrand: () => {
        this.form = {
          original: 'new',
          name: '',
          imageFile: '',
          version: 'v3',
          script: NEW_SCRIPT_TEMPLATE,
        };
        this.render();
      },
      save: () => void this.save(),
      remove: () => void this.removeBrand(),
      dryRun: () => void this.dryRun(),
    });
    this.onAct('input', {
      fName: (el) => (this.form!.name = (el as HTMLInputElement).value),
      fFile: (el) => (this.form!.imageFile = (el as HTMLInputElement).value),
      fVer: (el) => (this.form!.version = (el as HTMLInputElement).value),
      fScript: (el) => (this.form!.script = (el as HTMLTextAreaElement).value),
    });
  }

  private async save(): Promise<void> {
    const f = this.form;
    if (!f) return;
    const name = f.name.trim();
    if (!name) {
      showToast('Marka adı gerekli', 'warning');
      return;
    }
    const input: BrandProfileInput = {
      name,
      imageFile: f.imageFile.trim(),
      version: f.version.trim(),
      script: f.script,
    };
    try {
      if (f.original === 'new') {
        await brandService.create(input);
        showToast(`"${name}" markası eklendi`);
      } else {
        await brandService.update(f.original, input);
        showToast(`"${name}" profili kaydedildi`);
      }
      void this.load(name);
    } catch (e) {
      showToast(e instanceof Error && e.message ? e.message : 'Kaydedilemedi', 'warning');
    }
  }

  private async removeBrand(): Promise<void> {
    const f = this.form;
    if (!f || f.original === 'new') return;
    const brand = this.list.find((b) => b.name === f.original);
    if (brand && brand.deviceCount > 0) {
      showToast(`${brand.name} silinemez — ${nfmt(brand.deviceCount)} cihaz bu profili kullanıyor`, 'warning');
      return;
    }
    const ok = await confirmDialog({
      title: 'Marka silinsin mi?',
      message: `"${f.original}" markası ve imaj profili tanımı kalıcı olarak silinecek.`,
      tone: 'danger',
      icon: 'ti ti-trash',
      okLabel: 'Sil',
    });
    if (!ok) return;
    try {
      await brandService.remove(f.original);
      showToast(`"${f.original}" markası silindi`);
      void this.load();
    } catch {
      showToast('Silinemedi', 'warning');
    }
  }

  private async dryRun(): Promise<void> {
    const f = this.form;
    if (!f) return;
    try {
      const res = await brandService.validateScript(f.script);
      if (res.ok) showToast('Sözdizimi denetimi geçti — 0 hata', 'info');
      else showToast(`Script hatası: ${res.error ?? 'bilinmeyen'}`, 'warning');
    } catch {
      showToast('Denetim çalıştırılamadı', 'warning');
    }
  }

  protected override template(): SafeHtml {
    const editable = this.canEdit();

    return html`
      <div class="scr">
        <div style="flex:none;background:var(--vx-surface);border-bottom:1px solid var(--vx-border);padding:16px 16px 14px">
          <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap">
            <div>
              <div style="font:600 21px var(--vx-font-sans)">Marka & imaj yönetimi</div>
              <div style="font:400 12.5px/1.4 var(--vx-font-sans);color:var(--vx-text-2);margin-top:2px">Marka bazlı default imaj. Kurulum akışı: imaj iner → smg-player scripti koşar.</div>
            </div>
            ${when(editable, () => html`
              <button class="btn pri sm" style="margin-left:auto" data-act="newBrand"><i class="ti ti-plus"></i>Yeni marka</button>`)}
          </div>
        </div>

        ${this.bodyTemplate(editable)}
      </div>`;
  }

  private bodyTemplate(editable: boolean): SafeHtml {
    if (this.loading) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center">
        <i class="ti ti-loader-2 spin" style="font-size:26px;color:var(--vx-primary)"></i></div>`;
    }
    if (this.error) {
      return html`<div class="body" style="display:flex;align-items:center;justify-content:center;padding:24px">
        <div style="text-align:center;color:var(--vx-text-2)">
          <i class="ti ti-plug-connected-x" style="font-size:30px;color:var(--vx-text-3)"></i>
          <div style="font:500 13.5px var(--vx-font-sans);margin-top:8px">${this.error}</div>
        </div></div>`;
    }
    const f = this.form;
    return html`
      <div class="body" style="padding:16px;display:flex;flex-wrap:wrap;gap:16px;align-content:flex-start;overflow-x:hidden">
        <div class="card" style="flex:1 1 260px;padding:8px">
          <p class="sect" style="padding:8px 8px 4px;margin:0">Markalar</p>
          ${each(this.list, (b) => {
            const active = f?.original === b.name;
            return html`
              <button class="mrow" style="background:${active ? 'var(--vx-brand-50)' : 'transparent'}" data-act="pick" data-brand="${b.name}">
                <span style="width:34px;height:34px;border-radius:9px;background:${active ? 'var(--vx-brand-100)' : 'var(--vx-chip-bg)'};display:flex;align-items:center;justify-content:center;flex:none">
                  <i class="ti ti-photo" style="font-size:17px;color:${active ? 'var(--vx-primary)' : 'var(--vx-text-3)'}"></i>
                </span>
                <span style="flex:1;min-width:0">
                  <span style="display:block;font:600 13.5px var(--vx-font-sans);color:${active ? 'var(--vx-brand-700)' : 'var(--vx-text)'}">${b.name}</span>
                  <span class="mono" style="font-size:10.5px;color:var(--vx-text-3)">${b.imageFile} · ${b.version}</span>
                </span>
                <span class="chip mono" style="background:var(--vx-chip-bg);color:var(--vx-chip-fg)">${nfmt(b.deviceCount)}</span>
              </button>`;
          })}
          ${when(!this.list.length, () => html`
            <div style="padding:14px;color:var(--vx-text-3);font:400 12.5px var(--vx-font-sans)">Henüz marka tanımlı değil.</div>`)}
        </div>

        ${when(f, () => this.formCard(f!, editable))}
      </div>`;
  }

  private formCard(f: BrandForm, editable: boolean): SafeHtml {
    const isNew = f.original === 'new';
    const current = this.list.find((b) => b.name === f.original);
    const ro = editable ? '' : 'readonly';
    return html`
      <div class="card" style="flex:1 1 380px">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:15px">
          <span style="width:40px;height:40px;border-radius:var(--vx-radius-md);background:var(--vx-brand-50);display:flex;align-items:center;justify-content:center;flex:none">
            <i class="${isNew ? 'ti ti-plus' : 'ti ti-photo'}" style="font-size:20px;color:var(--vx-primary)"></i>
          </span>
          <div style="flex:1;min-width:0">
            <div style="font:600 15px var(--vx-font-sans)">${isNew ? 'Yeni marka' : `${f.name || '—'} — default imaj`}</div>
            <div class="mono" style="font-size:11px;color:var(--vx-text-3)">
              ${isNew ? 'Ad, imaj ve kurulum scriptini tanımla' : `${nfmt(current?.deviceCount ?? 0)} cihaza uygulanıyor`}
            </div>
          </div>
          ${when(editable && !isNew, () => html`
            <button class="btn dngo sm" data-act="remove"><i class="ti ti-trash"></i>Sil</button>`)}
        </div>
        <div style="display:flex;flex-direction:column;gap:13px">
          <div style="display:flex;gap:10px;flex-wrap:wrap">
            <div style="flex:1 1 150px"><label class="lbl">Marka adı</label>
              <input class="inp" data-act="fName" placeholder="Marka E" value="${f.name}" ${ro}></div>
            <div style="flex:0 1 130px"><label class="lbl">Sürüm</label>
              <input class="inp mono" data-act="fVer" placeholder="v3" value="${f.version}" ${ro}></div>
          </div>
          <div><label class="lbl">Default imaj dosyası</label>
            <input class="inp mono" data-act="fFile" placeholder="marka-e-rootfs.tar.gz" value="${f.imageFile}" ${ro}></div>
          <div>
            <label class="lbl" style="display:flex;align-items:center;gap:6px">
              <i class="ti ti-terminal-2" style="font-size:14px;color:var(--vx-text-3)"></i>Kurulum sonrası script — smg-player
            </label>
            <textarea class="inp mono" data-act="fScript" style="height:140px" ${ro}>${f.script}</textarea>
            <div style="font:400 11px/1.4 var(--vx-font-sans);color:var(--vx-text-3);margin-top:6px">
              İmaj yazıldıktan sonra cihazda çalışır. Değişkenler:
              <span class="mono">$SERIAL</span>, <span class="mono">$BRAND</span>, <span class="mono">$DEVICE_ID</span>.
            </div>
          </div>
          ${when(editable, () => html`
            <div style="display:flex;gap:10px;flex-wrap:wrap">
              <button class="btn sec" style="flex:1 1 130px" data-act="dryRun"><i class="ti ti-player-play"></i>Sınama (dry-run)</button>
              <button class="btn pri" style="flex:1 1 130px" data-act="save"><i class="ti ti-device-floppy"></i>${isNew ? 'Markayı ekle' : 'Profili kaydet'}</button>
            </div>`)}
          ${when(!editable, () => html`
            <div style="display:flex;gap:9px;align-items:flex-start;padding:12px;background:var(--vx-surface-2);border-radius:var(--vx-radius-md)">
              <i class="ti ti-lock" style="color:var(--vx-text-3);font-size:17px;flex:none"></i>
              <span style="font:400 12.5px/1.5 var(--vx-font-sans);color:var(--vx-text-2)">Marka ve imaj tanımlarını yalnızca admin değiştirebilir. Görüntüleme açıktır.</span>
            </div>`)}
        </div>
      </div>`;
  }
}

customElements.define('smg-brands-view', BrandsView);
